/* eslint camelcase: "off" */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  EmptyState,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@jem-open/jem-ui';
import { ArrowLeft, Folder, Info, Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { verificationClient } from '../../../../services/verificationClient';
import { PolicyRequirement } from '../../../../types/VerificationTypes';
import { buildSatisfiedByMap } from '../../VerificationAdmin/components/requirementsBlockHelpers';

import RequirementCategoriesSection from './RequirementCategoriesSection';


type View = 'requirements' | 'categories';


const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  identity: 'Verified identity primitives (selfie captured, ID number on file, biometric match, etc.).',
  step_up: 'Fresh authentication challenges — bank 2FA, OTP, PIN — that prove the employee is present right now.',
  consent: 'Terms & conditions, POPIA / privacy acknowledgements, and other consent gates the employee must have accepted.',
  status: 'Risk and compromise flags — employee not flagged risky, employer not compromised, account not blocked.',
  capability: 'Feature or permission gates — whether this employee is enrolled in / eligible for the action.',
  channel: 'Properties of the channel the request came through — verified WhatsApp identity, branded channel, etc.',
  session: 'Request-time context — amount thresholds, proxy detection, location, device fingerprint.',
};


const STRATEGY_DESCRIPTIONS: Record<string, string> = {
  ledger:
    'Backed by the EmployeeCredential ledger — checks whether a credential event (selfie captured, 2FA passed, T&Cs accepted, …) is on file and still within its TTL.',
  live_lookup:
    'Reads the source-of-truth model live at evaluation time (e.g. Employee.risky, Employer.is_compromised). No caching — always reflects current DB state.',
  session_context:
    'Read from the evaluation request itself — channel branding, channel identity, proxy flag, amount, location. Not persisted between requests.',
  composite:
    'AND/OR combination of other requirements. Forward-compatible seam — not yet wired to a resolver.',
};


/**
 * Read-only requirements registry. Mutations happen via Django seed migrations.
 *
 * Designed for embedding inside the Action Policies tab. Switches between
 * the requirements list and the requirement-categories registry via the
 * "View Categories" button.
 */
const RequirementsSection: React.FC = () => {
  const [view, setView] = useState<View>('requirements');
  const [rows, setRows] = useState<PolicyRequirement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await verificationClient.listRequirements());
    } catch (err) {
      apiResponseHandler.handleApiError(err);
      setError('Could not load requirements.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'requirements') load();
  }, [load, view]);

  const filtered = useMemo(() => {
    if (!filter) return rows;
    const q = filter.toLowerCase();
    return rows.filter(
      (r) =>
        r.code.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.category.code.toLowerCase().includes(q) ||
        (r.resolver_key || '').toLowerCase().includes(q) ||
        (r.remediation_flow || '').toLowerCase().includes(q)
    );
  }, [rows, filter]);

  // Inverse of PolicyRequirement.satisfies — computed from the full
  // registry, not the filtered view, so the "satisfied by" column stays
  // correct under filtering (the satisfier may itself be filtered out).
  const satisfiedByCode = useMemo(() => buildSatisfiedByMap(rows), [rows]);

  if (view === 'categories') {
    return (
      <div>
        <div className="mb-sm">
          <Button
            variant="link"
            size="sm"
            leftIcon={<ArrowLeft size={14} />}
            onClick={() => setView('requirements')}
          >
            Back to Requirements
          </Button>
        </div>
        <RequirementCategoriesSection />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-sm flex items-center gap-sm">
        <div className="flex-1">
          <SearchInput
            placeholder="Filter by code, name, category, resolver_key, or remediation flow…"
            value={filter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
            onClear={() => setFilter('')}
          />
        </div>
        <Button
          variant="outline"
          leftIcon={<Folder size={16} />}
          onClick={() => setView('categories')}
        >
          View Categories
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center py-lg">
          <Loader2 className="animate-spin text-secondary-text-label" size={24} />
        </div>
      )}
      {!loading && error && (
        <EmptyState
          variant="bordered"
          size="sm"
          icon="alert"
          title="Couldn’t load requirements"
          description={error}
          actions={<Button onClick={load}>Try again</Button>}
        />
      )}
      {!loading && !error && filtered.length === 0 && (
        <EmptyState
          variant="bordered"
          size="sm"
          icon="search"
          title="No requirements match"
          description="Try adjusting your filter, or clear it to see all requirements."
        />
      )}
      {!loading && !error && filtered.length > 0 && (
        <TooltipProvider delayDuration={150}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Strategy</TableHead>
                <TableHead>
                  <span className="inline-flex items-center gap-xxxs">
                    Remediation
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex cursor-help text-greyscale-text-caption">
                          <Info size={14} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        If the employee doesn&apos;t hold this credential, the chatbot launches the
                        named flow to capture it in-band. Rows with a dash can&apos;t be fixed in-band —
                        if the requirement is missing, the action is denied outright.
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
                <TableHead>Resolver Key</TableHead>
                <TableHead>TTL</TableHead>
                <TableHead>
                  <span className="inline-flex items-center gap-xxxs">
                    Satisfies
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex cursor-help text-greyscale-text-caption">
                          <Info size={14} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Subsumption between credentials. A stronger one
                        listed in <code className="font-mono">satisfies</code> covers any policy
                        asking for the weaker — e.g. holding{' '}
                        <code className="font-mono">whatsapp_id_verified</code> also satisfies{' '}
                        <code className="font-mono">whatsapp_id_uploaded</code>. The
                        <i> Satisfied by</i> line shows the inverse for this row.
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.code}>
                  <TableCell>
                    <code className="text-xs">{r.code}</code>
                  </TableCell>
                  <TableCell>
                    {CATEGORY_DESCRIPTIONS[r.category.code] ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">
                            <Tag variant="processing">{r.category.code}</Tag>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {CATEGORY_DESCRIPTIONS[r.category.code]}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tag variant="processing">{r.category.code}</Tag>
                    )}
                  </TableCell>
                  <TableCell>
                    {STRATEGY_DESCRIPTIONS[r.evaluation_strategy] ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">
                            <Tag variant="default">{r.evaluation_strategy}</Tag>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {STRATEGY_DESCRIPTIONS[r.evaluation_strategy]}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tag variant="default">{r.evaluation_strategy}</Tag>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.is_remediable && r.remediation_flow ? (
                      <code className="text-xs">{r.remediation_flow}</code>
                    ) : (
                      <span className="text-greyscale-text-caption">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.resolver_key ? <code className="text-xs">{r.resolver_key}</code> : '—'}
                  </TableCell>
                  <TableCell>
                    {r.default_ttl_seconds != null ? `${r.default_ttl_seconds}s` : '—'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const outbound = r.satisfies || [];
                      const inbound = satisfiedByCode.get(r.code) || [];
                      if (outbound.length === 0 && inbound.length === 0) {
                        return <span className="text-greyscale-text-caption">—</span>;
                      }
                      return (
                        <div className="flex flex-col gap-xxxs text-xs">
                          {outbound.length > 0 && (
                            <div>
                              <span className="text-greyscale-text-caption">Satisfies:</span>{' '}
                              {outbound.map((c, idx) => (
                                <React.Fragment key={c}>
                                  {idx > 0 && ', '}
                                  <code className="text-xs">{c}</code>
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                          {inbound.length > 0 && (
                            <div>
                              <span className="text-greyscale-text-caption">Satisfied by:</span>{' '}
                              {inbound.map((s, idx) => (
                                <React.Fragment key={s.code}>
                                  {idx > 0 && ', '}
                                  <code className="text-xs">{s.code}</code>
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TooltipProvider>
      )}
    </div>
  );
};

export default RequirementsSection;
